import React, { useEffect, useState } from 'react';
import {
    Alert,
    Box,
    Button,
    Dialog,
    DialogActions,
    DialogContent,
    DialogTitle,
    FormControl,
    FormControlLabel,
    FormLabel,
    Radio,
    RadioGroup,
    Typography,
} from '@mui/material';
import UploadFileIcon from '@mui/icons-material/UploadFile';
import { useTranslation } from 'react-i18next';
import { toast } from 'react-toastify';
import { BtnBase, ConfirmationDialog } from 'Components';
import { CERTIFICATE_FILE_ACCEPT, IMPORT_MODE_APPEND, IMPORT_MODE_REPLACE } from 'Constants/CertificateNames';
import { importIntoCertificateNameList } from 'Services/CertificateNamesSvc';
import ImportSummary from './ImportSummary';
import { apiErrorMessage } from './CertificateHelpers';

/**
 * Import a spreadsheet into an existing list.
 *
 * Always two steps. "Check file" sends the import as a dry run and shows the
 * server's summary — for a replace, including how many names would be removed.
 * Only then can it be sent for real, and a replace asks once more, naming the
 * list and the count it is about to remove. Changing the file or the mode
 * throws the check away, so what is confirmed is always what was checked.
 */
const CertificateListImportModal = ({ open, list, onClose, onImported }) => {
    const { t } = useTranslation();

    const [file, setFile] = useState(null);
    const [mode, setMode] = useState('');
    const [busy, setBusy] = useState(false);
    const [preview, setPreview] = useState(null);
    const [result, setResult] = useState(null);
    const [error, setError] = useState(null);
    const [confirmReplace, setConfirmReplace] = useState(false);

    useEffect(() => {
        if (open) {
            setFile(null);
            setMode('');
            setPreview(null);
            setResult(null);
            setError(null);
            setConfirmReplace(false);
        }
    }, [open, list]);

    const send = async (dryRun) => {
        setBusy(true);
        setError(null);
        try {
            const response = await importIntoCertificateNameList(list.id, { file, mode, dryRun });

            if (dryRun) {
                setPreview(response.import);
            } else {
                setResult(response.import);
                toast.success(
                    t(`admin.certificateNames.import.done.${mode}`, {
                        name: list.name,
                        imported: response.import?.imported ?? 0,
                        removed: response.import?.removed ?? 0,
                    }),
                );
                onImported(response.list);
            }
        } catch (requestError) {
            const message = apiErrorMessage(requestError, t('admin.certificateNames.import.failed'));
            setError(message);

            if (!dryRun) {
                toast.error(message);
            }
        }
        setBusy(false);
    };

    const handleImport = () => {
        if (mode === IMPORT_MODE_REPLACE) {
            setConfirmReplace(true);
        } else {
            send(false);
        }
    };

    const reset = () => {
        setPreview(null);
        setError(null);
    };

    if (!list) {
        return null;
    }

    return (
        <>
            <Dialog open={open} onClose={onClose} fullWidth maxWidth="md">
                <DialogTitle>{t('admin.certificateNames.import.title', { name: list.name })}</DialogTitle>
                <DialogContent dividers>
                    {result ? (
                        <ImportSummary summary={result} />
                    ) : (
                        <Box className="space-y-4">
                            <Typography variant="body2" color="text.secondary">
                                {t('admin.certificateNames.import.formatHint')}
                            </Typography>

                            <Button variant="outlined" component="label" startIcon={<UploadFileIcon />} disabled={busy}>
                                {file ? file.name : t('admin.certificateNames.import.chooseFile')}
                                <input
                                    hidden
                                    type="file"
                                    accept={CERTIFICATE_FILE_ACCEPT}
                                    onChange={(event) => {
                                        setFile(event.target.files?.[0] ?? null);
                                        event.target.value = '';
                                        reset();
                                    }}
                                />
                            </Button>

                            <FormControl>
                                <FormLabel>{t('admin.certificateNames.import.modeLabel')}</FormLabel>
                                <RadioGroup
                                    value={mode}
                                    onChange={(event) => {
                                        setMode(event.target.value);
                                        reset();
                                    }}
                                >
                                    <FormControlLabel
                                        value={IMPORT_MODE_APPEND}
                                        control={<Radio />}
                                        label={
                                            <span>
                                                <strong>{t('admin.certificateNames.import.modes.append')}</strong>
                                                {' — '}
                                                {t('admin.certificateNames.import.modes.appendHint')}
                                            </span>
                                        }
                                    />
                                    <FormControlLabel
                                        value={IMPORT_MODE_REPLACE}
                                        control={<Radio />}
                                        label={
                                            <span>
                                                <strong>{t('admin.certificateNames.import.modes.replace')}</strong>
                                                {' — '}
                                                {t('admin.certificateNames.import.modes.replaceHint', {
                                                    count: list.names_count,
                                                })}
                                            </span>
                                        }
                                    />
                                </RadioGroup>
                            </FormControl>

                            {error && <Alert severity="error">{error}</Alert>}

                            {mode === IMPORT_MODE_REPLACE && preview?.removed > 0 && (
                                <Alert severity="warning">
                                    {t('admin.certificateNames.import.replaceWarning', {
                                        removed: preview.removed.toLocaleString(),
                                        name: list.name,
                                    })}
                                </Alert>
                            )}

                            <ImportSummary summary={preview} />
                        </Box>
                    )}
                </DialogContent>
                <DialogActions sx={{ px: 3, py: 2 }}>
                    {result ? (
                        <BtnBase fullWidth={false} onClick={onClose}>
                            {t('common.close', 'Close')}
                        </BtnBase>
                    ) : (
                        <>
                            <BtnBase variant="text" fullWidth={false} onClick={onClose}>
                                {t('common.cancel')}
                            </BtnBase>
                            {!preview ? (
                                <BtnBase
                                    fullWidth={false}
                                    isLoading={busy}
                                    disabled={busy || !file || !mode}
                                    onClick={() => send(true)}
                                >
                                    {t('admin.certificateNames.import.check')}
                                </BtnBase>
                            ) : (
                                <BtnBase
                                    fullWidth={false}
                                    isLoading={busy}
                                    disabled={busy}
                                    color={mode === IMPORT_MODE_REPLACE ? 'error' : 'primary'}
                                    onClick={handleImport}
                                >
                                    {t(`admin.certificateNames.import.submit.${mode}`)}
                                </BtnBase>
                            )}
                        </>
                    )}
                </DialogActions>
            </Dialog>

            <ConfirmationDialog
                isOpen={confirmReplace}
                title={t('admin.certificateNames.import.confirmReplaceTitle')}
                text={t('admin.certificateNames.import.confirmReplaceText', {
                    name: list.name,
                    removed: (preview?.removed ?? 0).toLocaleString(),
                    final: (preview?.final_count ?? 0).toLocaleString(),
                })}
                onReject={() => setConfirmReplace(false)}
                onResolve={() => {
                    setConfirmReplace(false);
                    send(false);
                }}
            />
        </>
    );
};

export default CertificateListImportModal;
